
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslations } from 'use-intl';
import { RouterLinkAs } from '@/lib/router-link';
import {
  AlertDialog,
  Body,
  Box,
  Button,
  CalloutBar,
  Card,
  Heading,
  I,
  DropdownMenu,
  DropdownMenuItem,
  Pill,
  Popover,
  Row,
  Stack,
} from '@leanin/ui';
import {
  DefaultReactionDisc,
  DEFAULT_REACTION,
  REACTION_BY_ID,
  REACTION_LABEL_KEY,
  ReactionDisc,
  ReactionPicker,
  ReactionStack,
  chipPadding,
  type ReactionKind,
} from '../posts/index';

import { MemberRow } from '../MemberRow';
import type { FeedPost } from './types';
import {
  useDeleteFeedPost,
  useDismissFeedPost,
  usePollVote,
  useReactToFeedPost,
  useSaveFeedPost,
  useUnsaveFeedPost,
} from '../../lib/mutations/feed';
import { postContentSection } from '../../lib/analytics/events';
import { useViewer } from '../../lib/viewer-context';
import { useMember } from '../../lib/use-session';
import { PostComments } from './PostComments';
import { PostComposerSheet } from './PostComposer';
import { ReportContentDialog } from '../moderation/ReportContentDialog';
import { PostEntityCard } from './PostEntityCard';
import { PollCard } from './PollCard';
import { LinkPreviewCard } from '@/components/link-preview';
import { ReactionsModal } from './ReactionsModal';
import { ShareEntityModal } from './ShareEntityModal';
import { PostMediaGallery } from './PostMediaGallery';
import { ExpandableText } from './ExpandableText';
import { PostBodyRenderer } from '../editor';

type Props = {
  post: FeedPost;
  // When set, the scope pill matching this id is filtered out and a "Pinned"
  // indicator is shown if the post is pinned within that scope. Used when the
  // card is rendered inside a group's own detail page.
  hideScopeId?: string;
  // Unlocks leader-only overflow items (Pin/Unpin + Delete) in addition to
  // any author-only items. Pin/Unpin are only offered when the matching
  // handler is supplied.
  isLeader?: boolean;
  onPin?: () => void;
  onUnpin?: () => void;
  // Leader-only "Send as broadcast": opens the group's broadcast composer
  // prefilled from this post. Only offered when the handler is supplied —
  // same contract as Pin/Unpin above.
  onSendAsBroadcast?: () => void;
  // Platform-admin-only feature/unfeature of the platform-wide featured
  // slot. Only offered when the viewer is a platform admin AND the matching
  // handler is supplied — same contract as Pin/Unpin above.
  onFeature?: () => void;
  onUnfeature?: () => void;
  // Renders the entire body instead of the clamped feed preview. Set on the
  // post detail page; list surfaces keep the default preview.
  fullBody?: boolean;
  // Comment deep links (`/posts/{id}?comment=…`): open the comments section
  // from first render, and scroll/flash the target comment once loaded.
  initialCommentsOpen?: boolean;
  highlightCommentId?: string;
};

function messageFromError(err: unknown, fallback: string): string {
  if (err !== null && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return fallback;
}

const MAX_VISIBLE_SCOPE_PILLS = 3;

// Feed-preview clamp heights, LinkedIn-style: tighter when media or attached
// entities already give the card visual weight below the text.
const PREVIEW_LINES = 5;
const PREVIEW_LINES_WITH_ATTACHMENTS = 3;

// Touch devices can't hover the reaction summary pill open — a long-press
// (pointerdown held this long) opens the picker instead of the roster.
const LONG_PRESS_MS = 450;

function scopeHref(scope: FeedPost['scopes'][number]): string {
  if (scope.groupKind === 'network') return `/networks/${scope.groupId}`;
  if (scope.groupKind === 'space') {
    return `/groups/${scope.groupId}`;
  }
  return `/circles/${scope.groupId}`;
}

function scopeIcon(kind: FeedPost['scopes'][number]['groupKind']) {
  if (kind === 'network') return I.network;
  if (kind === 'space') return I.chat;
  return I.circles;
}

function scopeTone(
  kind: FeedPost['scopes'][number]['groupKind'],
): 'navy' | 'burgundy' | 'muted' {
  if (kind === 'network') return 'navy';
  if (kind === 'space') return 'muted';
  return 'burgundy';
}

function PostScopeRow({
  post,
  hideScopeId,
}: {
  post: FeedPost;
  hideScopeId?: string;
}) {
  const t = useTranslations('Feed');
  if (post.isGlobal) {
    return (
      <Row gap="sm" align="center">
        <Pill icon={I.globe}>{t('post.everyone')}</Pill>
      </Row>
    );
  }
  const scopes =
    hideScopeId !== undefined
      ? post.scopes.filter((s) => s.groupId !== hideScopeId)
      : post.scopes;
  if (scopes.length === 0) return null;
  const visible = scopes.slice(0, MAX_VISIBLE_SCOPE_PILLS);
  const overflow = scopes.length - visible.length;
  return (
    <Row gap="sm" align="center" style={{ flexWrap: 'wrap' }}>
      {visible.map((scope) => (
        <Pill
          key={scope.groupId}
          icon={scopeIcon(scope.groupKind)}
          href={scopeHref(scope)}
          linkAs={RouterLinkAs}
          tone={scopeTone(scope.groupKind)}
        >
          {scope.groupName}
        </Pill>
      ))}
      {overflow > 0 ? (
        <Pill>{t('post.moreCount', { count: overflow })}</Pill>
      ) : null}
    </Row>
  );
}

export const PostCard = ({
  post,
  hideScopeId,
  isLeader = false,
  onPin,
  onUnpin,
  onSendAsBroadcast,
  onFeature,
  onUnfeature,
  fullBody = false,
  initialCommentsOpen = false,
  highlightCommentId,
}: Props) => {
  // Participation is the server's call, not ours — `post.viewerCan` comes
  // straight from the rules `POST /posts/{id}/reactions` and
  // `POST /posts/{id}/comments` enforce. Reaction counts and the comment
  // thread stay visible either way; saving and sharing are read-gated, not
  // participation-gated, so they're always offered.
  const canReact = post.viewerCan.react;
  const canComment = post.viewerCan.comment;
  const t = useTranslations('Feed');
  const viewer = useViewer();
  const isAuthor = post.author.id === viewer.id;
  const isPlatformAdmin = useMember()?.platformRole === 'admin';
  const composerViewer = {
    id: viewer.id,
    name: viewer.displayName,
    ...(viewer.photoUrl !== null ? { photoUrl: viewer.photoUrl } : {}),
  };
  const pinnedInScope =
    hideScopeId !== undefined &&
    post.scopes.some(
      (s) => s.groupId === hideScopeId && s.pinnedAt !== null,
    );
  const contentSection = postContentSection(post);

  const reactMutation = useReactToFeedPost();
  const saveMutation = useSaveFeedPost();
  const pollVote = usePollVote();
  const unsaveMutation = useUnsaveFeedPost();
  const deleteMutation = useDeleteFeedPost();
  const dismissMutation = useDismissFeedPost();

  const [reactionCounts, setReactionCounts] = useState<Record<ReactionKind, number>>(
    post.reactionCounts,
  );
  const [viewerReaction, setViewerReaction] = useState<ReactionKind | null>(
    post.viewerReaction,
  );
  const [flashError, setFlashError] = useState<string | null>(null);

  const [showComments, setShowComments] = useState(initialCommentsOpen);
  // A `?comment=` deep link can arrive while this card is already mounted
  // (Back/Forward on the post page — route components never remount on a
  // search-only change). The panel must open for it, not only on mount.
  useEffect(() => {
    if (highlightCommentId !== undefined) setShowComments(true);
  }, [highlightCommentId]);
  const [commentCount, setCommentCount] = useState(post.comments);
  const [saved, setSaved] = useState(post.viewerSaved);
  const [shareOpen, setShareOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Touch long-press on the summary pill opens the picker; the flag
  // suppresses the click that fires on the subsequent pointerup so the
  // roster doesn't open on top of it.
  const longPressTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  useEffect(() => () => {
    if (pickerHoverTimeout.current !== null) clearTimeout(pickerHoverTimeout.current);
    if (longPressTimeout.current !== null) clearTimeout(longPressTimeout.current);
  }, []);
  const openPicker = () => {
    if (pickerHoverTimeout.current !== null) {
      clearTimeout(pickerHoverTimeout.current);
      pickerHoverTimeout.current = null;
    }
    setPickerOpen(true);
  };
  const schedulePicker = () => {
    pickerHoverTimeout.current = setTimeout(() => setPickerOpen(false), 160);
  };
  const onSummaryPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.pointerType !== 'touch') return;
    longPressTimeout.current = setTimeout(() => {
      longPressFired.current = true;
      openPicker();
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimeout.current !== null) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
  };
  const [reportOpen, setReportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [displayBody, setDisplayBody] = useState(post.body);
  const [displayBodyFormat, setDisplayBodyFormat] = useState(post.bodyFormat);
  const [displayMentions, setDisplayMentions] = useState(post.mentions);
  const [displayMedia, setDisplayMedia] = useState(post.media);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const reactionTotal =
    reactionCounts.support +
    reactionCounts.insightful +
    reactionCounts.same +
    reactionCounts.helpful;

  const onReact = (kind: ReactionKind) => {
    setFlashError(null);
    const previousCounts = reactionCounts;
    const previousViewerReaction = viewerReaction;
    const next: ReactionKind | null = previousViewerReaction === kind ? null : kind;
    const optimistic: Record<ReactionKind, number> = { ...previousCounts };
    if (previousViewerReaction !== null) {
      optimistic[previousViewerReaction] = Math.max(
        0,
        optimistic[previousViewerReaction] - 1,
      );
    }
    if (next !== null) optimistic[next] = optimistic[next] + 1;
    setReactionCounts(optimistic);
    setViewerReaction(next);

    reactMutation.mutate(
      { postId: post.id, kind, section: contentSection },
      {
        onSuccess: (result) => {
          setReactionCounts(result.reactionCounts);
          setViewerReaction(result.viewerReaction);
        },
        onError: (err) => {
          setReactionCounts(previousCounts);
          setViewerReaction(previousViewerReaction);
          setFlashError(messageFromError(err, t('post.errors.react')));
        },
      },
    );
  };

  const onToggleSave = () => {
    setFlashError(null);
    const previous = saved;
    const next = !previous;
    setSaved(next);
    const onError = (err: unknown) => {
      setSaved(previous);
      setFlashError(messageFromError(err, t('post.errors.save')));
    };
    if (next) {
      saveMutation.mutate(
        { postId: post.id, section: contentSection },
        { onError },
      );
    } else {
      unsaveMutation.mutate(post.id, { onError });
    }
  };

  const onConfirmDelete = () => {
    setDeleteOpen(false);
    deleteMutation.mutate(post.id, {
      onSuccess: () => {
        setDeleted(true);
      },
      onError: (err) => {
        setFlashError(messageFromError(err, t('post.errors.delete')));
      },
    });
  };

  if (deleted || dismissed) return null;

  const onDismiss = () => {
    // Optimistically hide; the server upsert is idempotent, so a failure
    // case (we still show toast / error) can be rolled back. Most users
    // won't see the post again either way.
    setDismissed(true);
    dismissMutation.mutate(
      { postId: post.id },
      {
        onError: (err) => {
          setDismissed(false);
          setFlashError(messageFromError(err, t('post.errors.hide')));
        },
      },
    );
  };

  const canModerate = isLeader || isAuthor;

  // React ACTION chip (engagement line, left) — LinkedIn split: this chip
  // only ever reacts. One disc (yours when reacted, tinted; empty otherwise)
  // + total count. Click toggles Love / removes your reaction; hover or
  // touch long-press opens the picker. Who-reacted lives in the separate
  // discs cluster on the right.
  const reactionActionChip = (
    <Button
      variant="secondary"
      size="sm"
      style={
        viewerReaction !== null
          ? {
              ...chipPadding,
              background: REACTION_BY_ID[viewerReaction].fill,
              color: REACTION_BY_ID[viewerReaction].ink,
              // Full shorthand, not borderColor: the secondary variant sets
              // `border`, and React drops a longhand override on rerender
              // when it conflicts with an earlier shorthand.
              border: `1px solid ${REACTION_BY_ID[viewerReaction].ink}`,
              borderRadius: 'var(--radius-full)',
            }
          : chipPadding
      }
      aria-label={
        viewerReaction !== null
          ? t('reactions.removeYours', {
              reaction: t(REACTION_LABEL_KEY[viewerReaction]),
            })
          : t('post.reactAria')
      }
      aria-pressed={viewerReaction !== null}
      disabled={!canReact}
      onClick={() => {
        if (longPressFired.current) {
          // The long-press already opened the picker — swallow the click
          // that follows pointerup.
          longPressFired.current = false;
          return;
        }
        setPickerOpen(false);
        // Toggle: remove the held reaction, or Love by default (the picker
        // handles the other kinds).
        onReact(viewerReaction ?? DEFAULT_REACTION);
      }}
      {...(canReact
        ? {
            onPointerDown: onSummaryPointerDown,
            onPointerUp: cancelLongPress,
            onPointerCancel: cancelLongPress,
            onPointerLeave: cancelLongPress,
          }
        : {})}
    >
      <Row gap="sm" align="center">
        {viewerReaction !== null ? (
          <ReactionDisc reaction={REACTION_BY_ID[viewerReaction]} size={18} />
        ) : (
          <DefaultReactionDisc size={18} />
        )}
        <span>{reactionTotal > 0 ? reactionTotal : t('actions.react')}</span>
      </Row>
    </Button>
  );

  return (
    <Card pad="flush" style={{ overflow: 'hidden' }}>
      {post.featuredAt !== null ? (
        <CalloutBar tone="poppy" icon={I.spark} label={t('post.featured')} />
      ) : null}
      {post.recommended !== undefined ? (
        <CalloutBar
          tone="neutral"
          icon={I.spark}
          label={t('post.recommended')}
          meta={post.recommended.reason}
        />
      ) : null}
      {pinnedInScope ? (
        <CalloutBar tone="poppy" icon={I.pin} label={t('post.pinnedByLeaders')} />
      ) : null}

      <Stack gap="md" p="lg">
        <MemberRow
          userId={post.author.id}
          {...(post.author.photo ? { photo: post.author.photo } : {})}
          name={post.author.name}
          nameFont="var(--type-title-sm)"
          sublabel={
            post.author.role !== undefined && post.author.role.length > 0
              ? `${post.author.role} · ${post.time}`
              : post.time
          }
          avatarSize={44}
          right={
            <DropdownMenu
              trigger={
                <Button variant="ghost" size="sm" icon={I.more} aria-label={t('post.moreActionsAria')} />
              }
            >
              <DropdownMenuItem onSelect={() => setShareOpen(true)}>
                {t('post.menu.share')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onToggleSave}>
                {saved === true ? t('post.menu.saved') : t('post.menu.save')}
              </DropdownMenuItem>
              {isAuthor ? (
                <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                  {t('post.menu.edit')}
                </DropdownMenuItem>
              ) : null}
              {isLeader && onUnpin !== undefined && pinnedInScope ? (
                <DropdownMenuItem onSelect={onUnpin}>{t('post.menu.unpin')}</DropdownMenuItem>
              ) : null}
              {isLeader && onPin !== undefined && !pinnedInScope ? (
                <DropdownMenuItem onSelect={onPin}>{t('post.menu.pin')}</DropdownMenuItem>
              ) : null}
              {isLeader && onSendAsBroadcast !== undefined ? (
                <DropdownMenuItem onSelect={onSendAsBroadcast}>
                  {t('post.menu.sendAsBroadcast')}
                </DropdownMenuItem>
              ) : null}
              {isPlatformAdmin && onUnfeature !== undefined && post.featuredAt !== null ? (
                <DropdownMenuItem onSelect={onUnfeature}>
                  {t('post.menu.unfeature')}
                </DropdownMenuItem>
              ) : null}
              {isPlatformAdmin && onFeature !== undefined && post.featuredAt === null ? (
                <DropdownMenuItem onSelect={onFeature}>
                  {t('post.menu.feature')}
                </DropdownMenuItem>
              ) : null}
              {!isAuthor ? (
                <DropdownMenuItem onSelect={onDismiss}>
                  {t('post.menu.hide')}
                </DropdownMenuItem>
              ) : null}
              {!isAuthor ? (
                <DropdownMenuItem onSelect={() => setReportOpen(true)}>
                  {t('post.menu.report')}
                </DropdownMenuItem>
              ) : null}
              {canModerate ? (
                <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                  {t('post.menu.delete')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenu>
          }
        />

        <PostScopeRow
          post={post}
          {...(hideScopeId !== undefined ? { hideScopeId } : {})}
        />

        {post.title !== undefined ? (
          <Heading level={3}>
            {post.title}
          </Heading>
        ) : null}

        {editOpen ? (
          <PostComposerSheet
            viewer={composerViewer}
            mode="edit"
            editPostId={post.id}
            initialBody={displayBody}
            initialBodyFormat={displayBodyFormat}
            initialMentions={displayMentions}
            initialMedia={displayMedia}
            initialAttached={post.attachedEntities}
            chrome="flush"
            onClose={() => setEditOpen(false)}
            onSaved={(updated) => {
              setDisplayBody(updated.body);
              setDisplayBodyFormat(updated.bodyFormat);
              setDisplayMentions(updated.mentions);
              setDisplayMedia(updated.media);
              setEditOpen(false);
            }}
          />
        ) : fullBody ? (
          <PostBodyRenderer bodyFormat={displayBodyFormat} mentions={displayMentions}>
            {displayBody}
          </PostBodyRenderer>
        ) : (
          <ExpandableText
            lines={
              displayMedia.length > 0 || post.attachedEntities.length > 0
                ? PREVIEW_LINES_WITH_ATTACHMENTS
                : PREVIEW_LINES
            }
            moreLabel={t('post.seeMore')}
          >
            <PostBodyRenderer bodyFormat={displayBodyFormat} mentions={displayMentions}>
              {displayBody}
            </PostBodyRenderer>
          </ExpandableText>
        )}

        {post.attachedEntities.length > 0 ? (
          <div
            style={{
              display: 'grid',
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
              gap: 'var(--space-sm)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            {post.attachedEntities.map((entity, idx) => (
              <PostEntityCard key={`${entity.kind}:${entity.id}:${idx}`} entity={entity} />
            ))}
          </div>
        ) : null}

        {post.linkPreviews.length > 0 ? (
          <div
            style={{
              display: 'grid',
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
              gap: 'var(--space-sm)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            {post.linkPreviews.map((preview, idx) => (
              <LinkPreviewCard key={`${preview.url}:${idx}`} preview={preview} />
            ))}
          </div>
        ) : null}

        {!editOpen && displayMedia.length > 0 ? (
          <PostMediaGallery items={displayMedia} />
        ) : null}

        {post.poll ? (
          <PollCard
            postId={post.id}
            poll={post.poll}
            question={post.body}
            pending={pollVote.isPending}
            onVote={(optionId) =>
              pollVote.mutate({
                postId: post.id,
                optionId,
                section: contentSection,
              })
            }
          />
        ) : null}

        {flashError !== null ? (
          <Body color="destructive">
            {flashError}
          </Body>
        ) : null}
      </Stack>

      {/* Engagement line — replaces the old chips row; Share and Save moved
          into the header kebab. Left cluster = actions (react, comment);
          right = the reaction discs, the who-reacted roster trigger. */}
      <Row
        py="md"
        px="xl"
        align="center"
        justify="space-between"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <Row gap="sm" align="center">
          {canReact ? (
            <Popover
              open={pickerOpen}
              onOpenChange={(open) => { if (!open) setPickerOpen(false); }}
              allowOverflow
              trigger={
                <span onMouseEnter={openPicker} onMouseLeave={schedulePicker}>
                  {reactionActionChip}
                </span>
              }
              side="top"
              align="start"
            >
              <div onMouseEnter={openPicker} onMouseLeave={schedulePicker}>
                <ReactionPicker
                  mine={viewerReaction}
                  onPick={(kind) => {
                    onReact(kind);
                    setPickerOpen(false);
                  }}
                />
              </div>
            </Popover>
          ) : (
            reactionActionChip
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={I.chat}
            onClick={() => setShowComments((s) => !s)}
            style={chipPadding}
            aria-expanded={showComments}
            aria-label={t('actions.comment')}
          >
            {t('actions.commentCount', { count: commentCount })}
          </Button>
        </Row>
        {reactionTotal > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            style={{
              ...chipPadding,
              // The ghost button's padding is invisible — pull it out so the
              // DISCS sit at the row's inset, mirroring the left chip's
              // visible border.
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- negative offset (undo the ghost Button's padding) has no SpaceKey equivalent
              marginInlineEnd: 'calc(-1 * var(--space-sm))',
            }}
            aria-label={t('reactions.seeWhoAria')}
            onClick={() => setReactionsOpen(true)}
          >
            <ReactionStack
              counts={reactionCounts}
              mine={viewerReaction}
              size={18}
            />
          </Button>
        ) : null}
      </Row>

      {showComments ? (
        <Box
          p="lg"
          style={{
            borderTop: '1px solid var(--border)',
          }}
        >
          <PostComments
            postId={post.id}
            authorUserId={post.author.id}
            section={contentSection}
            canComment={canComment}
            canReact={canReact}
            onCommentAdded={() => setCommentCount((c) => c + 1)}
            onCommentDeleted={(threadSize) =>
              setCommentCount((c) => Math.max(0, c - threadSize))
            }
            {...(highlightCommentId !== undefined
              ? { highlightCommentId }
              : {})}
            {...(!post.isGlobal && post.scopes.length === 1
              ? { audienceGroupId: post.scopes[0]!.groupId }
              : {})}
          />
        </Box>
      ) : null}

      <ReportContentDialog
        isOpen={reportOpen}
        onOpenChange={setReportOpen}
        targetKind="post"
        targetId={post.id}
        subjectName={post.author.name}
      />

      {/* Mount-gated like ShareEntityButton's modal — no resolver query or
          roster fetch until the menu item / pill is actually used. */}
      {shareOpen ? (
        <ShareEntityModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          entity={{ kind: 'post', id: post.id }}
        />
      ) : null}
      {reactionsOpen ? (
        <ReactionsModal
          target={{ kind: 'post', id: post.id }}
          counts={reactionCounts}
          open={reactionsOpen}
          onOpenChange={setReactionsOpen}
        />
      ) : null}

      <AlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('post.deleteDialog.title')}
        description={t('post.deleteDialog.description')}
        actionLabel={t('post.deleteDialog.confirm')}
        cancelLabel={t('post.deleteDialog.cancel')}
        destructive
        onAction={onConfirmDelete}
      />
    </Card>
  );
};
